import React from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

type Props = {
  children: React.ReactNode;
  /** 前提条件（箇条書き文字列の配列） */
  prerequisites?: string[];
  /** 完了条件（箇条書き文字列の配列） */
  completionCriteria?: string[];
};

/**
 * 前提条件・完了条件の共通サブセクション
 */
const ConditionSection: React.FC<{
  icon: React.ReactNode;
  title: string;
  items: string[];
  borderColor: string;
}> = ({ icon, title, items, borderColor }) => (
  <Box
    sx={{
      borderLeft: 3,
      borderColor,
      borderRadius: 1,
      pl: 1.5,
      py: 0.5,
    }}
  >
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
      {icon}
      <Typography variant="caption" fontWeight={600}>
        {title}
      </Typography>
    </Stack>
    <Box component="ul" sx={{ m: 0, pl: 2 }}>
      {items.map((item, idx) => (
        <Box component="li" key={idx} sx={{ mb: 0.25 }}>
          <Typography variant="caption" color="text.secondary">
            {item}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

/**
 * 記事のゴールを目立たせる緑系のボックス
 * 前提条件・完了条件が渡された場合はゴール内に統一レイアウトで表示する
 */
const WorkflowGoalBox: React.FC<Props> = ({ children, prerequisites, completionCriteria }) => {
  return (
    <Box
      sx={{
        borderLeft: 4,
        borderColor: 'success.main',
        borderRadius: 1,
        p: 2,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <EmojiObjectsIcon sx={{ fontSize: 22, color: 'success.main', mt: 0.1 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            このステップのゴール
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {children}
          </Typography>

          {(prerequisites || completionCriteria) && (
            <Stack spacing={1.5} sx={{ mt: 2 }}>
              {prerequisites && (
                <ConditionSection
                  icon={<PlaylistAddCheckIcon sx={{ fontSize: 16, color: 'warning.main' }} />}
                  title="前提条件"
                  items={prerequisites}
                  borderColor="warning.main"
                />
              )}
              {completionCriteria && (
                <ConditionSection
                  icon={<CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'info.main' }} />}
                  title="完了条件"
                  items={completionCriteria}
                  borderColor="info.main"
                />
              )}
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default WorkflowGoalBox;
